import React from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import Compressor from 'compressorjs';
import {
  COUNTRY_CODES,
  IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB,
  IDENTITY_VERIFICATION_PHOTOS_QUALITY,
} from '../../../utils/constants';
import {useComputedReservationDetails} from '../../../context/computedReservationDetails';
import {getIdentificaiontEventName} from '../utils';
import {getFileSizeMB, toBase64} from '../../../utils/common';
import {getHousingCountryCode} from '../../../utils/reservation';
import {setCurrentEvent} from '../../../store/slices/identityVerificationParamSlice';
import UploadFile from '../../acommon/FieldUploadFile';
import styles from '../ConfirmIdentityTemplate.module.sass';
import {Buttons, ActivityIndicator} from './';

const pdfUploadingAllowedCountries = [COUNTRY_CODES.uae];

const UploadPhotoComponent = React.memo((props: any) => {
  const Content = React.memo(
    ({
      screenshot,
      setScreenshot,
      validationError,
      setValidationError,
      activityIndicator,
      stateName,
    }: any) => {
      const {t} = useTranslation();
      const dispatch = useDispatch();
      const {housing} = useComputedReservationDetails();
      const housingCountry = getHousingCountryCode(housing);

      const params = {
        eventName: getIdentificaiontEventName(stateName),
        ...(stateName === 'frontSideDocumentPhoto' && {type: 'Front'}),
        ...(stateName === 'backSideDocumentPhoto' && {type: 'Back'}),
        picture: 'Uploaded',
      };

      const handleImage = (image?: File) => {
        if (!image) return;
        return new Compressor(image, {
          quality: IDENTITY_VERIFICATION_PHOTOS_QUALITY,
          success: (compressedResult: File) => {
            toBase64(compressedResult)
              .then(result => {
                dispatch(setCurrentEvent(params));
                setScreenshot(result);
              })
              .catch(console.error);
          },
        });
      };

      const validationImage = (image?: File) => {
        const fileSize = getFileSizeMB(image);

        if (!image) return false;

        if (pdfUploadingAllowedCountries.includes(housingCountry)) {
          if (!/(\.jpg|\.jpeg|\.png|\.pdf)/i.test(image?.name || '')) {
            setValidationError(t('incorrect_file_type_only_pdf_and_file_types_allowed'));
            return false;
          }

          setValidationError(null);
          return true;
        }

        if (!/(\.jpg|\.jpeg|\.png)/i.test(image?.name || '')) {
          setValidationError(t('incorrect_file_type_only_file_types_allowed'));
          return false;
        }

        if (!fileSize || fileSize > IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB) {
          setValidationError(
            t('max_file_size_is_number_mb', {
              number: IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB,
            }),
          );
          return false;
        }

        setValidationError(null);
        return true;
      };

      const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const image = e.currentTarget?.files?.[0];
        const isSuccessfullyValidated = validationImage(image);
        if (isSuccessfullyValidated) handleImage(image);
      };

      if (activityIndicator)
        return <ActivityIndicator activityIndicator={activityIndicator} />;
      if (screenshot)
        return <img className={styles.screenshot} src={screenshot} alt="Front side" />;

      return <UploadFile error={validationError} onChange={handleUpload} />;
    },
  );

  return (
    <>
      <Content
        stateName={props.stateName}
        screenshot={props.screenshot}
        setScreenshot={props.setScreenshot}
        activityIndicator={props.activityIndicator}
        validationError={props.validationError}
        setValidationError={props.setValidationError}
      />
      <Buttons
        withCapture={false}
        screenshot={props.screenshot}
        onSend={props.onSend}
        onCapture={props.onCapture}
        isCaptureButtonDisabled={props.isCaptureButtonDisabled}
        onRetry={props.onRetry}
        disabled={props.disabled}
      />
    </>
  );
});

export {UploadPhotoComponent};
